import { useState, useEffect, useRef } from "react"
import { Box } from "@mui/material"
import { footerToHtml } from "@crm-src/components/webinar-builder/footer-section"
import { EMAIL_DOCUMENT_OPEN, EMAIL_DOCUMENT_CLOSE } from "@crm-src/components/brochure-builder/email-template"
import { CtaSection, ctaToHtml } from "@crm-src/components/brochure-builder/cta-section"
import { type UseCaseSection, type TenantInfo } from "@crm-src/components/usecase-brochure-builder/model"
import { HeroSection, heroToHtml } from "@crm-src/components/usecase-brochure-builder/hero-section"
import { ChallengeSection, challengeToHtml } from "@crm-src/components/usecase-brochure-builder/challenge-section"
import { ComparisonSection, comparisonToHtml } from "@crm-src/components/usecase-brochure-builder/comparison-section"
import { StepsSection, stepsToHtml } from "@crm-src/components/usecase-brochure-builder/steps-section"

interface UseCaseBrochureBuilderProps {
  tenantName: string
  tenantInfo?: TenantInfo
  initialSections?: UseCaseSection[]
  onContentChange: (html: string) => void
  onSectionsChange?: (sections: UseCaseSection[]) => void
}

// An editorial, photo-led problem→solution brochure email (modeled on the
// "Hospitality Emergency Communications" collateral). Like the other section
// builders it emits a COMPLETE HTML document rendered verbatim by crm-api, and
// it deliberately holds to a clean layout: ONE photo focal point + ONE title on
// the hero, one balanced lead, a balanced two-column comparison with dash
// bullets (no "blob" markers), and a single focal two-step flow.
export function UseCaseBrochureBuilder({
  tenantName,
  tenantInfo,
  initialSections,
  onContentChange,
  onSectionsChange,
}: UseCaseBrochureBuilderProps) {
  const getDefaultSections = (): UseCaseSection[] => [
    {
      id: "hero",
      type: "hero",
      content: {
        imageUrl: "/stock-images/emergency-vehicles.jpg",
        eyebrow: "Beyond the hotel room",
        brandTag: "Mass Notification",
        title: "Hospitality Emergency Communications",
        subtitle: "When an alarm sounds, safety can't stop at the room door — reach every guest and every staff member, wherever they are.",
      },
    },
    {
      id: "challenge",
      type: "challenge",
      content: {
        heading: "The challenge",
        // Both source paragraphs, verbatim. A blank line separates them so each
        // renders as its own <p> — nothing is condensed away.
        body: "In an emergency, PBX systems play a critical role. They let hotel staff initiate broadcast calls to guest rooms, delivering immediate alerts about fire alarms, evacuation instructions, or security threats. This reaches guests quickly through in-room phones and ensures a baseline of awareness.\n\nHowever, legacy PBX systems are location bound. They are built around fixed endpoints — guest room phones or desk extensions. Once a guest leaves their room, enters a hallway, exits the building, or is in transit, that channel disappears. The same gap applies to staff moving across large properties, outdoor areas, or off-site — and it becomes critically dangerous in fast-moving emergencies. History shows hotel emergencies often evolve rapidly and unpredictably, far beyond the reach of static systems.",
      },
    },
    {
      id: "comparison",
      type: "comparison",
      content: {
        leftTitle: "Requirements",
        leftItems: "Role-specific instructions for staff (security vs. housekeeping vs. management)\nGuest evacuation routes that adapt to location — in multiple languages\nCommunication that persists beyond the physical hotel infrastructure",
        rightTitle: "Legacy PBX issues",
        rightItems: "Endpoint dependency — limited to registered phones or devices\nLimited mobility — alerts don't follow users across locations\nSingle-channel — primarily voice-based alerts\nNo real-time coordination — hard to issue evolving instructions",
      },
    },
    {
      id: "steps",
      type: "steps",
      content: {
        heading: "The XOP solution",
        lead: "A coordinated two-step response — the PBX raises the first alarm where guests already are; USN carries the conversation everywhere they go next.",
        step1Title: "PBX initiates the alert",
        step1Role: "Immediate · in-room",
        step1Points: "Room phones ring instantly\nGuests receive immediate notification",
        step2Title: "USN takes over communication",
        step2Role: "Mobile · everywhere next",
        step2Points: "Follow-up messages via SMS & mobile apps\nReal-time updates as the situation evolves\nCoordination among staff and responders",
      },
    },
    {
      id: "cta",
      type: "cta",
      content: {
        heading: "Keep guests safe — wherever they are.",
        subtext: "See XOP mass notification for hospitality in a short walkthrough with our team.",
        ctaLabel: "Get in touch",
        ctaUrl: "",
      },
    },
  ]

  const ensureAllSections = (saved: UseCaseSection[] | undefined): UseCaseSection[] => {
    if (!saved || saved.length === 0) return getDefaultSections()
    const defaults = getDefaultSections()
    return defaults.map((def) => saved.find((s) => s.type === def.type) ?? def)
  }

  const [sections, setSections] = useState<UseCaseSection[]>(() => ensureAllSections(initialSections))
  const lastSectionsJsonRef = useRef<string>("")
  const lastContentHtmlRef = useRef<string>("")

  const updateSection = (sectionId: string, newContent: any) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, content: { ...section.content, ...newContent } } : section,
      ),
    )
  }

  useEffect(() => {
    if (initialSections && initialSections.length > 0) {
      setSections(ensureAllSections(initialSections))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSections])

  const generateHtmlFromSections = (toRender: UseCaseSection[]): string => {
    let html = EMAIL_DOCUMENT_OPEN
    toRender.forEach((section) => {
      switch (section.type) {
        case "hero":
          html += heroToHtml(section, tenantName)
          break
        case "challenge":
          html += challengeToHtml(section)
          break
        case "comparison":
          html += comparisonToHtml(section)
          break
        case "steps":
          html += stepsToHtml(section)
          break
        case "cta":
          html += ctaToHtml(section as any)
          break
      }
    })
    html += footerToHtml(
      { id: "footer", type: "footer", content: {} } as any,
      tenantName,
      tenantInfo,
      new Date().getFullYear(),
    )
    html += EMAIL_DOCUMENT_CLOSE
    return html
  }

  useEffect(() => {
    const sectionsJson = JSON.stringify(sections)
    if (sectionsJson === lastSectionsJsonRef.current) return
    lastSectionsJsonRef.current = sectionsJson

    const html = generateHtmlFromSections(sections)
    if (html !== lastContentHtmlRef.current) {
      lastContentHtmlRef.current = html
      onContentChange(html)
    }
    if (onSectionsChange) onSectionsChange(sections)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, onContentChange, onSectionsChange])

  const renderSection = (section: UseCaseSection) => {
    switch (section.type) {
      case "hero":
        return <HeroSection section={section} onUpdate={updateSection} />
      case "challenge":
        return <ChallengeSection section={section} onUpdate={updateSection} />
      case "comparison":
        return <ComparisonSection section={section} onUpdate={updateSection} />
      case "steps":
        return <StepsSection section={section} onUpdate={updateSection} />
      case "cta":
        return <CtaSection section={section as any} onUpdate={updateSection} />
      default:
        return null
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", bgcolor: "background.paper", maxWidth: "800px", mx: "auto", width: "100%" }}>
        {sections.map((section) => (
          <div key={section.id}>{renderSection(section)}</div>
        ))}
      </Box>
    </Box>
  )
}
